/**
 * The action catalogue (#1798) — the single declaration behind this app's
 * resource routes, their sidebar rows, their list → detail navigation and their
 * Drawer/Page surface decision.
 *
 * Every `permission` below is written out in full. They are IDENTICAL to the
 * values the hand-written `<PermissionGuard>`s and sidebar rows carried before
 * the derivation landed — this issue changed how a permission is DECLARED, not
 * what any of them means, and `src/__tests__/actionLayerSnapshot.test.ts`
 * compares the derived sets against a baseline captured from the pre-change
 * tree, item by item.
 *
 * Menu `order` is the position WITHIN a section; the rows a section has that
 * are not backed by an action carry their own `order` in `sidebar-config.tsx`
 * and interleave with these. Menu CONTENT and GROUPING follow owner decisions
 * (#1775 / #1777, then the v4 shell of owner 2026-10-03, #2476), and
 * `actionLayerSnapshot.test.ts` pins them row by row against a baseline plus
 * each IA change declared there one by one — a row moved here without such a
 * declaration fails that test.
 */
import {
  Boxes,
  CalendarRange,
  ChartNoAxesCombined,
  Car,
  FileCheck,
  Package,
  ShoppingCart,
  Users,
  UtensilsCrossed,
} from 'lucide-react';
import { ResourcePage, ResourceDetailPage } from '@resource-kit/ui/react';
import type { AnyFieldDef } from '@resource-kit/model';
import type { ResourceBundle } from '@resource-kit/controllers/react';
import { productResource } from '@features/products';
import { customerResource } from '@features/customers';
import { driverResource } from '@features/drivers';
import { DriverProfilesPage } from '@features/driver-profiles';
import { orderResource } from '@features/orders';
import {
  ingredientResource,
  supplierResource,
  dishResource,
  comboResource,
} from '@features/catalog';
import { menuPlanResource, MenuPlanListPage } from '@features/menu-planning';
import { jobResource } from '@features/dispatch';
import { AnalyticsPage } from '@features/analytics';
import { NavGroupTabs } from '@components/layout/NavGroupTabs';
import type { NavGroupId } from '@components/layout/nav-groups';
import { DETAIL_SURFACE } from '@resource-kit/core';
import { PERMISSIONS } from '@/permissions';
import { MENU_SECTION } from './menu-sections';
import { defineActions, type ActionView } from './defineActions';

// The two shared shells every resource-backed route renders. Both helpers stay
// generic in the record and field types so each bundle keeps its own — the
// alternative, a common `ResourceBundle<unknown, ...>`, would only typecheck by
// widening.
const listView = <T, F extends Record<string, AnyFieldDef>>(
  bundle: ResourceBundle<T, F>,
): ActionView => ({
  render: () => <ResourcePage bundle={bundle} />,
});

/**
 * `listView` for a page that belongs to a merged sidebar entry: the group's tab
 * strip rides in the masthead via `host.listTabs`. The override lives here, not
 * on the resource, because the strip is a fact about the PAGE (which merged
 * entry this list sits under), not about the resource — the same reasoning as
 * `features/audit/AuditPages.tsx`. Detail views carry no strip.
 */
const groupListView = <T, F extends Record<string, AnyFieldDef>>(
  bundle: ResourceBundle<T, F>,
  group: NavGroupId,
): ActionView => ({
  render: () => (
    <ResourcePage
      bundle={{
        ...bundle,
        host: { ...bundle.host, listTabs: () => <NavGroupTabs group={group} /> },
      }}
    />
  ),
});

const detailView = <T, F extends Record<string, AnyFieldDef>>(
  bundle: ResourceBundle<T, F>,
): ActionView => ({
  render: () => <ResourceDetailPage bundle={bundle} />,
});

export const customerActions = defineActions(
  { name: 'customers' },
  {
    main: {
      path: 'customers',
      permission: { list: PERMISSIONS.CUSTOMERS_READ, read: PERMISSIONS.CUSTOMERS_READ },
      detailSurface: DETAIL_SURFACE.both,
      views: { list: listView(customerResource), detail: detailView(customerResource) },
      menu: {
        section: MENU_SECTION.ordersProducts,
        order: 20,
        labelKey: 'nav.customers',
        icon: <Users className="w-4 h-4" />,
      },
    },
  },
);

export const productActions = defineActions(
  { name: 'products' },
  {
    main: {
      path: 'products',
      permission: { list: PERMISSIONS.PRODUCTS_READ, read: PERMISSIONS.PRODUCTS_READ },
      detailSurface: DETAIL_SURFACE.both,
      views: { list: listView(productResource), detail: detailView(productResource) },
      menu: {
        section: MENU_SECTION.ordersProducts,
        order: 30,
        labelKey: 'nav.products',
        icon: <Package className="w-4 h-4" />,
      },
    },
  },
);

export const orderActions = defineActions(
  { name: 'orders' },
  {
    main: {
      path: 'orders',
      permission: { list: PERMISSIONS.ORDERS_READ, read: PERMISSIONS.ORDERS_READ },
      detailSurface: DETAIL_SURFACE.both,
      views: { list: listView(orderResource), detail: detailView(orderResource) },
      menu: {
        section: MENU_SECTION.ordersProducts,
        order: 10,
        labelKey: 'nav.orders',
        icon: <ShoppingCart className="w-4 h-4" />,
      },
    },
  },
);

export const driverActions = defineActions(
  { name: 'drivers' },
  {
    main: {
      path: 'drivers',
      permission: { list: PERMISSIONS.DISPATCH_READ, read: PERMISSIONS.DISPATCH_READ },
      detailSurface: DETAIL_SURFACE.both,
      views: {
        list: groupListView(driverResource, 'drivers'),
        detail: detailView(driverResource),
      },
      // The merged 司机与司机画像 entry (#2476): the profiles list is its second
      // tab, so it has no row of its own.
      menu: {
        section: MENU_SECTION.delivery,
        order: 40,
        labelKey: 'nav.driversAndProfiles',
        icon: <Car className="w-4 h-4" />,
      },
    },
  },
);

// Drawer-only: the profile opens in place, and there has never been a
// `/driver-profiles/:id` route. The surface decision is what withholds it.
export const driverProfileActions = defineActions(
  { name: 'driverProfiles' },
  {
    main: {
      path: 'driver-profiles',
      permission: {
        list: PERMISSIONS.DISPATCH_READ,
        read: PERMISSIONS.DISPATCH_READ,
        update: PERMISSIONS.DISPATCH_WRITE,
      },
      detailSurface: DETAIL_SURFACE.drawer,
      views: { list: { render: () => <DriverProfilesPage /> } },
    },
  },
);

// Kitchen — the 厨房管理 group, restored by the owner on 2026-10-03 (#2476,
// epic #2469 C.1). Dishes and ingredients are the landing tabs of two merged
// entries (菜品与套餐 / 食材与库存), so they carry the rows; combos and
// suppliers are tabs only (see `components/layout/nav-groups.ts`).
export const ingredientActions = defineActions(
  { name: 'ingredients' },
  {
    main: {
      path: 'catalog/ingredients',
      permission: {
        list: PERMISSIONS.CATALOG_READ,
        read: PERMISSIONS.CATALOG_READ,
        create: PERMISSIONS.CATALOG_WRITE,
        update: PERMISSIONS.CATALOG_WRITE,
      },
      detailSurface: DETAIL_SURFACE.both,
      views: {
        list: groupListView(ingredientResource, 'ingredientsStock'),
        detail: detailView(ingredientResource),
      },
      menu: {
        section: MENU_SECTION.kitchen,
        order: 30,
        labelKey: 'nav.kitchen.ingredientsStock',
        icon: <Boxes className="w-4 h-4" />,
      },
    },
  },
);

export const supplierActions = defineActions(
  { name: 'suppliers' },
  {
    main: {
      path: 'catalog/suppliers',
      permission: {
        list: PERMISSIONS.CATALOG_READ,
        read: PERMISSIONS.CATALOG_READ,
        create: PERMISSIONS.CATALOG_WRITE,
        update: PERMISSIONS.CATALOG_WRITE,
      },
      detailSurface: DETAIL_SURFACE.both,
      views: {
        list: groupListView(supplierResource, 'procurementSuppliers'),
        detail: detailView(supplierResource),
      },
    },
  },
);

export const dishActions = defineActions(
  { name: 'dishes' },
  {
    main: {
      path: 'catalog/dishes',
      permission: {
        list: PERMISSIONS.CATALOG_READ,
        read: PERMISSIONS.CATALOG_READ,
        create: PERMISSIONS.CATALOG_WRITE,
        update: PERMISSIONS.CATALOG_WRITE,
      },
      detailSurface: DETAIL_SURFACE.both,
      views: {
        list: groupListView(dishResource, 'dishesCombos'),
        detail: detailView(dishResource),
      },
      menu: {
        section: MENU_SECTION.kitchen,
        order: 20,
        labelKey: 'nav.kitchen.dishesCombos',
        icon: <UtensilsCrossed className="w-4 h-4" />,
      },
    },
  },
);

export const comboActions = defineActions(
  { name: 'combos' },
  {
    main: {
      path: 'catalog/combos',
      permission: {
        list: PERMISSIONS.CATALOG_READ,
        read: PERMISSIONS.CATALOG_READ,
        create: PERMISSIONS.CATALOG_WRITE,
        update: PERMISSIONS.CATALOG_WRITE,
      },
      detailSurface: DETAIL_SURFACE.both,
      views: {
        list: groupListView(comboResource, 'dishesCombos'),
        detail: detailView(comboResource),
      },
    },
  },
);

// The list is a bespoke page, the detail the shared shell. The week view hanging
// off `…/:id/week` is a third surface with no place in a two-surface action, so
// it stays a hand-written route in `App.tsx`.
export const menuPlanActions = defineActions(
  { name: 'menuPlans' },
  {
    main: {
      path: 'menu-planning/plans',
      permission: { list: PERMISSIONS.CATALOG_READ, read: PERMISSIONS.CATALOG_READ },
      detailSurface: DETAIL_SURFACE.both,
      views: { list: { render: () => <MenuPlanListPage /> }, detail: detailView(menuPlanResource) },
      menu: {
        section: MENU_SECTION.kitchen,
        order: 10,
        labelKey: 'nav.menuPlanning.plans',
        icon: <CalendarRange className="w-4 h-4" />,
      },
    },
  },
);

export const jobActions = defineActions(
  { name: 'jobs' },
  {
    main: {
      path: 'dispatch/jobs',
      permission: {
        list: PERMISSIONS.DISPATCH_READ,
        read: PERMISSIONS.DISPATCH_READ,
        update: PERMISSIONS.DISPATCH_WRITE,
      },
      detailSurface: DETAIL_SURFACE.both,
      views: { list: listView(jobResource), detail: detailView(jobResource) },
      menu: {
        section: MENU_SECTION.delivery,
        order: 20,
        labelKey: 'nav.jobs',
        icon: <FileCheck className="w-4 h-4" />,
      },
    },
  },
);

export const analyticsActions = defineActions(
  { name: 'analytics' },
  {
    main: {
      path: 'analytics',
      permission: {
        list: PERMISSIONS.ANALYTICS_READ,
        read: PERMISSIONS.ANALYTICS_READ,
      },
      detailSurface: DETAIL_SURFACE.drawer,
      views: { list: { render: () => <AnalyticsPage /> } },
      menu: {
        section: MENU_SECTION.workbench,
        order: 50,
        labelKey: 'nav.analytics',
        icon: <ChartNoAxesCombined className="w-4 h-4" />,
      },
    },
  },
);

const ACTION_LAYERS = [
  customerActions,
  productActions,
  orderActions,
  driverActions,
  driverProfileActions,
  ingredientActions,
  supplierActions,
  dishActions,
  comboActions,
  menuPlanActions,
  jobActions,
  analyticsActions,
] as const;

/** Every derived route, router-neutral. `App.tsx` adapts these to React Router. */
export const APP_ACTION_ROUTES = ACTION_LAYERS.flatMap((layer) => [...layer.routes]);

/** Every derived sidebar row. `sidebar-config.tsx` places them by section. */
export const APP_ACTION_MENU_ITEMS = ACTION_LAYERS.flatMap((layer) => [...layer.menuItems]);
